import { HideAmountsButton, useAmountsMaskVersion } from "@/components/common/HideAmountsButton";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { useViewScope } from "@/context/ViewScopeContext";
import HeaderProfileGroup from "@/components/TopBar";
import { useEffect, useMemo, useState } from "react";
import { ChevronDown, User, Globe, Users } from "lucide-react";
import api from "@/lib/api";
import { DASH_MODULES, DASH_COMING, ModuleCard, ComingSoonCard, AttentionTable, PeopleRanking } from "@/components/dashboard/DashboardWidgets";

/** Spec iter282 — sélecteur de vue admin : Ma vue / Globale / [dernier utilisateur ▾]. Contrôle segmenté (spec iter185). */
function ViewSwitch({ view, targetId, users, onChange }) {
  const [open, setOpen] = useState(false);
  const target = users.find((u) => u.id === targetId);
  const seg = (active) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 11px", fontSize: 13, fontWeight: 500, borderRadius: 7, background: active ? "#1d2b3a" : "transparent", color: active ? "#8fbce8" : "#9a9aa0", transition: "background-color .15s, color .15s" });
  return (
    <div className="relative inline-flex items-center" style={{ background: "#141416", border: "1px solid #2a2a2e", borderRadius: 9, padding: 3, gap: 2 }} data-testid="dash-view-switch">
      <button type="button" style={seg(view === "me")} onClick={() => onChange("me", null)} data-testid="dash-view-me"><User size={14} /> Ma vue</button>
      <button type="button" style={seg(view === "global")} onClick={() => onChange("global", null)} data-testid="dash-view-global"><Globe size={14} /> Globale</button>
      <button type="button" style={seg(view === "user")}
        onClick={() => (target && view !== "user" ? onChange("user", target.id) : setOpen((o) => !o))} data-testid="dash-view-user">
        <Users size={14} /> {view === "user" && target ? `${target.first_name} ${target.last_name}` : "Utilisateur"} <ChevronDown size={12} onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-52 max-h-64 overflow-y-auto rounded-md border border-white/10 bg-[#161616] py-1 shadow-xl" data-testid="dash-view-user-menu">
          {users.map((u) => (
            <button key={u.id} type="button" className="block w-full text-left px-3 py-1.5 text-[12px] hover:bg-white/5"
              onClick={() => { onChange("user", u.id); setOpen(false); }} data-testid={`dash-view-user-${u.id}`}>{u.first_name} {u.last_name}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  useAmountsMaskVersion();
  const { user } = useAuth();
  const firstName = user?.first_name || "";
  const navigate = useNavigate();
  const { setViewScope } = useViewScope();
  const isAdmin = user?.role === "admin";
  // Spec iter298 — préférences de vue mémorisées par utilisateur (localStorage).
  const prefsKey = `ledesk_dash_prefs_${user?.id || "anon"}`;
  const saved = useMemo(() => { try { return JSON.parse(localStorage.getItem(prefsKey) || "{}"); } catch { return {}; } }, [prefsKey]);
  const [view, setView] = useState(() => (saved.view === "user" && !saved.targetId ? "me" : saved.view || "me"));
  const [targetId, setTargetId] = useState(saved.targetId || null);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState(null);
  const [people, setPeople] = useState(null);
  const [moduleFilter, setModuleFilter] = useState(() => (saved.module && saved.module !== "all" ? { key: saved.module, t: 0 } : null));
  const [activeModule, setActiveModule] = useState(saved.module || "all");
  const [periods, setPeriods] = useState(() => ({ ...Object.fromEntries(DASH_MODULES.map((m) => [m.key, "week"])), ...(saved.periods || {}) }));
  useEffect(() => {
    if (!user?.id) return;
    localStorage.setItem(prefsKey, JSON.stringify({ view, targetId, module: activeModule, periods }));
  }, [prefsKey, user?.id, view, targetId, activeModule, periods]);

  useEffect(() => {
    let alive = true;
    const params = view === "user" ? { view, user_id: targetId } : { view };
    api.get("/dashboard/overview", { params }).then(({ data }) => { if (alive) setData(data); }).catch((e) => { if (alive && e?.response?.status === 403) setData({ denied: e.response.data?.detail || "Tableau de bord non autorisé.", cards: [] }); });
    return () => { alive = false; };
  }, [view, targetId]);
  // Spec iter327 — « Action par personne » : requête globale lancée EN PARALLÈLE de l'overview
  // principal (plus d'attente en cascade) ; en vue globale, réutilise la réponse principale.
  useEffect(() => {
    if (!isAdmin) return;
    if (view === "global") { if (data?.attention_by_person) setPeople(data.attention_by_person); return; }
    let alive = true;
    api.get("/dashboard/overview", { params: { view: "global" } })
      .then(({ data: g }) => { if (alive) setPeople(g?.attention_by_person || []); }).catch(() => {});
    return () => { alive = false; };
  }, [isAdmin, view, view === "global" ? data : null]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users/directory")
      .then(({ data }) => setUsers((data || []).filter((u) => u.is_active && u.id !== user?.id)
        .sort((a, b) => `${a.first_name}`.localeCompare(b.first_name, "fr")))).catch(() => {});
  }, [isAdmin, user?.id]);
  // Ligne du panneau « Action par personne » → ouvre le module filtré sur la personne (vue Utilisateur) et ses éléments en attente.
  const openModuleFor = (id, mod) => {
    setViewScope(id === user?.id ? "mine" : `user:${id}`);
    if (mod === "taches") navigate("/taches");
    else if (mod === "documents") navigate("/documents?scope=my_in_progress");
    else navigate(`/avi?subtype=${{ accuses: "accuse", validations: "validation", information: "information" }[mod]}&scope=waiting_me`);
  };
  // Clic sur un avatar « Action par personne » → la table « Action requise » bascule sur la vue de cette personne.
  const [personSel, setPersonSel] = useState(null);
  const selectPerson = (p) => {
    if (!p) { setPersonSel(null); setView("me"); return; }
    setPersonSel(p);
    if (p.user_id === user?.id) setView("me"); else { setView("user"); setTargetId(p.user_id); }
  };
  useEffect(() => { if (personSel && !(view === "user" && targetId === personSel.user_id) && !(view === "me" && personSel.user_id === user?.id)) setPersonSel(null); }, [view, targetId]); // eslint-disable-line react-hooks/exhaustive-deps
  const attentionN = data?.attention_count ?? 0;
  const hasCard = (c) => !data?.cards || data.cards.includes(c); // iter448 4b — cartes autorisées côté serveur
  const perfOf = useMemo(() => (k) => data?.perf?.breakdown?.[k] ?? null, [data]);

  const todayRaw = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const today = todayRaw.charAt(0).toUpperCase() + todayRaw.slice(1);
  const hour = new Date().getHours();
  const greeting = hour >= 5 && hour < 12 ? "Bonjour" : hour >= 12 && hour < 18 ? "Bon après-midi" : "Bonsoir";
  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 64px)", minHeight: 0, overflow: "hidden", gap: 14 }} data-testid="dashboard-page">
      {/* En-tête aligné à gauche (spec C1 — 2026-09-30) : salutation dynamique + date FR ; « Mon indice » déplacé en carte */}
      <div className="flex items-start justify-between gap-4 shrink-0" data-testid="dashboard-header">
        <div className="flex flex-col min-w-0">
          <h1 className="tracking-tight leading-tight" style={{ fontSize: 22, fontWeight: 700, color: "#f2f2f5" }} data-testid="dashboard-greeting">
            {greeting}, <span style={{ color: "#0078D4" }}>{firstName}</span>
          </h1>
          <div style={{ fontSize: 13, color: "#8a8a8f", marginTop: 2 }} data-testid="dashboard-date">{today}</div>
          <p className="text-muted-foreground text-sm mt-2" data-testid="dashboard-attention-line">
            {data && !hasCard("greeting") ? "" : data ? (
              <><span className="font-semibold" style={{ color: attentionN ? "#f87171" : "#5dcaa5" }}>{attentionN}</span>
                {" "}point{attentionN > 1 ? "s" : ""} {attentionN > 1 ? "nécessitent" : "nécessite"} votre attention aujourd'hui{view === "global" ? " (toute l'équipe)" : ""}.</>
            ) : "Chargement…"}
          </p>
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <HideAmountsButton />
          {isAdmin && <ViewSwitch view={view} targetId={targetId} users={users} onChange={(v, id) => { setView(v); if (id) setTargetId(id); }} />}
          <HeaderProfileGroup />
        </div>
      </div>

      {data?.denied && <div style={{ padding: 30, textAlign: "center", color: "#F87171", fontSize: 12 }} data-testid="general-dash-403">{data.denied}</div>}
      {/* Cartes module — spec iter282 */}
      {hasCard("modules") && <section className="space-y-3 shrink-0" data-testid="dash-modules">
        <div className="grid grid-cols-1 md:grid-cols-4 xl:grid-cols-7 gap-4 dash-kpi-grid">
          {DASH_MODULES.map((m) => (
            <ModuleCard key={m.key} mod={m} data={data?.modules?.[m.key]} pct={perfOf(m.key)}
              period={periods[m.key]} onPeriod={(p) => setPeriods({ ...periods, [m.key]: p })} />
          ))}
          {DASH_COMING.map((m) => <ComingSoonCard key={m.key} mod={m} />)}
        </div>
      </section>}

      {!hasCard("greeting") ? null : isAdmin ? (
        <div className="grid gap-4 items-stretch flex-1 min-h-0" style={{ gridTemplateColumns: "minmax(0, 72fr) minmax(0, 26fr)" }} data-testid="dash-action-grid">
          <div className="min-w-0 min-h-0 flex flex-col">
            <AttentionTable items={data?.attention || []} byModule={data?.attention_by_module || {}} moduleFilter={moduleFilter} onModuleChange={setActiveModule}
              viewIsDefault={view === "me"} onReset={() => { setView("me"); setModuleFilter(null); setPersonSel(null); }} person={personSel} onClearPerson={() => selectPerson(null)} />
          </div>
          <div className="flex flex-col gap-4 min-w-0 self-stretch min-h-0" style={{ overflow: "hidden" }} data-testid="dash-right-column">
            <div className="min-h-0 flex-1 flex flex-col"><PeopleRanking people={people === null ? null : people} onPickModule={openModuleFor} selectedId={personSel?.user_id || null} onSelect={selectPerson} /></div>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 items-stretch flex-1 min-h-0" style={{ gridTemplateColumns: "minmax(0, 72fr) minmax(0, 26fr)" }} data-testid="dash-action-grid">
          <div className="min-w-0 min-h-0 flex flex-col">
            <AttentionTable items={data?.attention || []} byModule={data?.attention_by_module || {}} moduleFilter={moduleFilter} onModuleChange={setActiveModule}
              viewIsDefault={view === "me"} onReset={() => setView("me")} />
          </div>
          <div className="flex flex-col gap-4 min-w-0 self-stretch" data-testid="dash-right-column" />
        </div>
      )}

    </div>
  );
}
